import { type ChangeEvent, type FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import PasswordInputField from '@/components/feature/PasswordInputField';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import {
  changeMemberPassword,
  createMemberChallenge,
  deleteMemberChallenge,
  fetchPrivateDiscussionThreads,
  fetchMemberChallenges,
  fetchMemberDashboard,
  fetchMemberPushConfiguration,
  fetchMemberOrders,
  fetchMemberProfile,
  fetchShopProductById,
  joinMemberChallenge,
  type CreateMemberChallengeInput,
  type MemberDashboard,
  type MemberOrderItem,
  type PublicChallengeItem,
  type PublicAuthUser,
  type PrivateDiscussionThread,
  updateMemberAvatar,
  updateMemberChallenge,
  updateMemberProfile,
  uploadPublicProfileImage,
  uploadCommunityImage
} from '@/features/public/api';
import {
  persistMemberDashboardSnapshot,
  persistMemberSession,
  readMemberAccessToken,
  readStoredMemberDashboard,
  readStoredMemberUser,
  signOutMemberSession,
  withMemberSession as withStoredMemberSession
} from '@/features/public/memberSession';
import { formatEventDateTimeRange } from '@/shared/events/dateRange';
import { disableMemberPushNotifications, enableMemberPushNotifications, supportsMemberPushNotifications } from '@/shared/push/memberPushNotifications';
import { useRealtimeInvalidation } from '@/shared/realtime/useRealtimeInvalidation';
import { getPasswordValidationError, passwordRuleMessage } from '@/shared/validation/password';

const fallbackStats = {
  availableEvents: 0,
  eventsAttended: 0,
  challengesCompleted: 0,
  squadsJoined: 0,
  teamsJoined: 0,
  points: 0,
  rank: 'Membre Bronze',
  nextRank: 'Membre Argent',
  pointsToNext: 500,
};

const CHALLENGE_SPORT_OPTIONS = [
  'Football',
  'Basketball',
  'Volleyball',
  'Tennis de Table',
  'Natation',
  'Athletisme',
  'Marche / Course',
  'Gaming',
];

function createDefaultChallengeDeadline() {
  const date = new Date();
  date.setDate(date.getDate() + 14);
  return date.toISOString().split('T')[0] ?? '';
}

function createDefaultChallengeForm(): CreateMemberChallengeInput {
  return {
    title: '',
    description: '',
    sport: CHALLENGE_SPORT_OPTIONS[0] ?? 'Football',
    target: 1000,
    unit: '',
    reward: '',
    deadline: createDefaultChallengeDeadline(),
  };
}

function createChallengeFormFromItem(challenge: PublicChallengeItem): CreateMemberChallengeInput {
  return {
    title: challenge.title,
    description: challenge.description,
    sport: challenge.sport,
    image: challenge.image ?? undefined,
    target: challenge.target,
    unit: challenge.unit,
    reward: challenge.reward,
    deadline: challenge.deadline,
  };
}

type MemberTab = 'dashboard' | 'profile' | 'orders' | 'settings' | 'challenges';

const shortcuts = [
  { id: '1', name: 'Mes défis', icon: 'ri-trophy-line', color: 'bg-orange-500', link: '/membre?tab=challenges' },
  { id: '2', name: 'Mes effectifs', icon: 'ri-team-line', color: 'bg-teal-600', link: '/communaute/squads' },
  { id: '3', name: 'Résultats', icon: 'ri-bar-chart-line', color: 'bg-blue-500', link: '/sport/resultats' },
  { id: '4', name: 'Documents', icon: 'ri-file-list-line', color: 'bg-purple-500', link: '/club' },
];

function resolveMemberTab(value: string | null): MemberTab {
  if (value === 'challenges' || value === 'profile' || value === 'orders' || value === 'settings') {
    return value;
  }

  return 'dashboard';
}

function formatChallengeDeadline(value: string) {
  return new Date(value).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

function createStoredUserFromDashboard(
  profile: NonNullable<MemberDashboard['profil']>,
  existingUser?: PublicAuthUser | null
): PublicAuthUser {
  return {
    id: profile.id,
    email: profile.email,
    matricule: profile.matricule,
    mustChangePassword: existingUser?.mustChangePassword ?? false,
    prenom: profile.prenom,
    nom: profile.nom,
    telephone: profile.telephone,
    departement: profile.departement,
    role: 'MEMBER',
    statut: profile.statut,
    avatar: profile.avatar,
    profil: null,
  };
}

export default function MemberPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<MemberTab>(() => resolveMemberTab(searchParams.get('tab')));
  const [user, setUser] = useState<PublicAuthUser | null>(() => readStoredMemberUser());
  const [dashboard, setDashboard] = useState<MemberDashboard | null>(() => readStoredMemberDashboard());
  const [orders, setOrders] = useState<MemberOrderItem[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<MemberOrderItem | null>(null);
  const [orderProductFallbacks, setOrderProductFallbacks] = useState<Record<string, { image: string | null; description: string | null; category: string | null; badge: string | null }>>({});
  const [challenges, setChallenges] = useState<PublicChallengeItem[]>([]);
  const [privateThreads, setPrivateThreads] = useState<PrivateDiscussionThread[]>([]);
  const [loading, setLoading] = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [challengesLoading, setChallengesLoading] = useState(false);
  const [joiningChallengeId, setJoiningChallengeId] = useState<string | null>(null);
  const [deletingChallengeId, setDeletingChallengeId] = useState<string | null>(null);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [challengeFeedback, setChallengeFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [challengeForm, setChallengeForm] = useState<CreateMemberChallengeInput>(() => createDefaultChallengeForm());
  const [editingChallenge, setEditingChallenge] = useState<PublicChallengeItem | null>(null);
  const [challengeImageFile, setChallengeImageFile] = useState<File | null>(null);
  const [challengeImagePreview, setChallengeImagePreview] = useState<string | null>(null);
  const [challengeImageRemoved, setChallengeImageRemoved] = useState(false);
  const [creatingChallenge, setCreatingChallenge] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [updatingAvatar, setUpdatingAvatar] = useState(false);
  const [updatingProfile, setUpdatingProfile] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushRequesting, setPushRequesting] = useState(false);
  const [pushError, setPushError] = useState<string | null>(null);
  const [profileFeedback, setProfileFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [settingsFeedback, setSettingsFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [profileForm, setProfileForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordErrors, setPasswordErrors] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const heroAvatarInputRef = useRef<HTMLInputElement | null>(null);
  const challengeImageObjectUrlRef = useRef<string | null>(null);
  const challengeComposerRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const [memberRealtimeToken, setMemberRealtimeToken] = useState<string | null>(() => readMemberAccessToken());
  const pushSupported = supportsMemberPushNotifications();
  const forcePasswordChange = Boolean(user?.mustChangePassword || searchParams.get('forcePassword') === '1');
  const shouldShowForcePasswordModal = forcePasswordChange && Boolean(user);

  const upcomingEvents = dashboard?.upcomingEvents ?? [];
  const recentActivities = [
    ...(dashboard?.upcomingEvents ?? []).map((event) => ({
      id: `event-${event.id}`,
      type: 'event',
      title: event.titre,
      date: formatEventDateTimeRange(event),
      status: 'à venir'
    })),
    ...(dashboard?.latestNews ?? []).map((news) => ({
      id: `news-${news.id}`,
      type: 'news',
      title: news.titre,
      date: news.date,
      status: 'publié'
    }))
  ].slice(0, 4);

  const formatMoney = (value: number) =>
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF', maximumFractionDigits: 0 }).format(value);

  const formatStatus = (status: MemberOrderItem['status']) => {
    switch (status) {
      case 'PENDING':
        return { label: 'En attente', classes: 'bg-amber-100 text-amber-700' };
      case 'CONFIRMED':
        return { label: 'Confirmée', classes: 'bg-blue-100 text-blue-700' };
      case 'SHIPPED':
        return { label: 'Expédiée', classes: 'bg-purple-100 text-purple-700' };
      case 'DELIVERED':
        return { label: 'Livrée', classes: 'bg-green-100 text-green-700' };
      case 'CANCELLED':
        return { label: 'Annulée', classes: 'bg-red-100 text-red-700' };
      default:
        return { label: status, classes: 'bg-gray-100 text-gray-700' };
    }
  };

  const formatPaymentStatus = (status: MemberOrderItem['paymentStatus']) => {
    switch (status) {
      case 'PROCESSING':
        return { label: 'Paiement en cours', classes: 'bg-blue-100 text-blue-700' };
      case 'SUCCEEDED':
        return { label: 'Paiement validé', classes: 'bg-emerald-100 text-emerald-700' };
      case 'FAILED':
        return { label: 'Paiement échoué', classes: 'bg-red-100 text-red-700' };
      default:
        return { label: 'Paiement en attente', classes: 'bg-amber-100 text-amber-700' };
    }
  };

  const orderStatusFlow: Array<{
    key: MemberOrderItem['status'];
    label: string;
    icon: string;
  }> = [
    { key: 'PENDING', label: 'En attente', icon: 'ri-time-line' },
    { key: 'CONFIRMED', label: 'Confirmée', icon: 'ri-checkbox-circle-line' },
    { key: 'SHIPPED', label: 'Expédiée', icon: 'ri-truck-line' },
    { key: 'DELIVERED', label: 'Livrée', icon: 'ri-home-smile-line' },
  ];

  const getOrderStatusIndex = (status: MemberOrderItem['status']) => {
    return orderStatusFlow.findIndex((step) => step.key === status);
  };

  const isEditingChallenge = Boolean(editingChallenge);

  const getPasswordInputClassName = (hasError: boolean) =>
    `w-full rounded-xl border px-4 py-3 text-sm text-gray-900 outline-none transition ${
      hasError
        ? 'border-red-300 bg-red-50/40 focus:border-red-400'
        : 'border-gray-200 bg-white focus:border-orange-400'
    }`;

  useEffect(() => {
    const nextTab = resolveMemberTab(searchParams.get('tab'));
    if (nextTab !== activeTab) {
      setActiveTab(nextTab);
    }
  }, [activeTab, searchParams]);

  useEffect(() => {
    if (!user) {
      return;
    }

    setProfileForm({
      firstName: user.prenom ?? '',
      lastName: user.nom ?? '',
      email: user.email ?? '',
    });
  }, [user]);

  useEffect(() => {
    if (!shouldShowForcePasswordModal || typeof document === 'undefined') {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [shouldShowForcePasswordModal]);

  const revokeChallengeImageObjectUrl = useCallback(() => {
    if (challengeImageObjectUrlRef.current) {
      URL.revokeObjectURL(challengeImageObjectUrlRef.current);
      challengeImageObjectUrlRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      revokeChallengeImageObjectUrl();
    };
  }, [revokeChallengeImageObjectUrl]);

  useEffect(() => {
    if (!selectedOrder) {
      return;
    }

    const productsToHydrate = selectedOrder.items
      .filter((item) => item.productId && (!item.imageUrl || !item.description))
      .map((item) => item.productId as string)
      .filter((productId, index, array) => array.indexOf(productId) === index)
      .filter((productId) => !orderProductFallbacks[productId]);

    if (!productsToHydrate.length) {
      return;
    }

    let isCancelled = false;

    void (async () => {
      const results = await Promise.all(
        productsToHydrate.map(async (productId) => {
          try {
            const product = await fetchShopProductById(productId);
            return {
              productId,
              product
            };
          } catch {
            return null;
          }
        })
      );

      if (isCancelled) {
        return;
      }

      setOrderProductFallbacks((current) => {
        const next = { ...current };

        results.forEach((result) => {
          if (!result) {
            return;
          }

          next[result.productId] = {
            image: result.product.image,
            description: result.product.description,
            category: result.product.category,
            badge: result.product.badge ?? null
          };
        });

        return next;
      });
    })();

    return () => {
      isCancelled = true;
    };
  }, [orderProductFallbacks, selectedOrder]);

  const changeTab = (tab: MemberTab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  const loadSession = useCallback(async () => {
    let isMounted = true;

    try {
      setSessionError(null);
      setLoading(true);
      setOrdersLoading(true);

      const storedUser = readStoredMemberUser();
      const storedDashboard = readStoredMemberDashboard();

      if (storedUser && isMounted) {
        setUser(storedUser);
      }
      if (storedDashboard && isMounted) {
        setDashboard(storedDashboard);
      }

      const payload = await withStoredMemberSession(async (token) => {
          setMemberRealtimeToken(token);
          const profile = await fetchMemberProfile(token);
          persistMemberSession(token, profile);

          if (profile.mustChangePassword) {
            return {
              token,
              profile,
              memberDashboard: null,
              memberOrders: [],
              memberChallenges: [],
              memberPrivateThreads: [],
            };
          }

          const memberDashboard = await fetchMemberDashboard(token);

          let memberOrders: MemberOrderItem[] = [];
          try {
            const response = await fetchMemberOrders(token, { limit: 10 });
            memberOrders = response.items;
          } catch {
            memberOrders = [];
          }

          let memberChallenges: PublicChallengeItem[] = [];
          try {
            memberChallenges = await fetchMemberChallenges(token);
          } catch {
            memberChallenges = [];
          }

          let memberPrivateThreads: PrivateDiscussionThread[] = [];
          try {
            memberPrivateThreads = await fetchPrivateDiscussionThreads(token);
          } catch {
            memberPrivateThreads = [];
          }

          return {
            token,
            profile,
            memberDashboard,
            memberOrders,
            memberChallenges,
            memberPrivateThreads,
          };
        });

      if (!payload) {
        navigate('/formulaires?tab=login');
        return;
      }

      if (!isMounted) {
        return;
      }

      setDashboard(payload.memberDashboard);
      if (payload.memberDashboard) {
        persistMemberDashboardSnapshot(payload.memberDashboard);
      }
      setOrders(payload.memberOrders);
      setChallenges(payload.memberChallenges);
      setPrivateThreads(payload.memberPrivateThreads);

      if (payload.memberDashboard?.profil) {
        const nextUser = createStoredUserFromDashboard(payload.memberDashboard.profil, payload.profile);
        setUser(nextUser);
        persistMemberSession(payload.token, nextUser);
      } else {
        setUser(payload.profile);
      }
    } catch (caughtError) {
      if (isMounted) {
        setSessionError(
          caughtError instanceof Error
            ? caughtError.message
            : "Impossible de charger votre espace membre pour le moment. Réessayez dans quelques instants."
        );
      }
    } finally {
      if (isMounted) {
        setOrdersLoading(false);
        setLoading(false);
      }
    }

    return () => {
      isMounted = false;
    };
  }, [navigate]);

  useEffect(() => {
    let cleanup: (() => void) | undefined;

    void (async () => {
      cleanup = await loadSession();
    })();

    return () => {
      cleanup?.();
    };
  }, [loadSession]);

  useRealtimeInvalidation({
    topics: ["community-stats", "challenges", "matches", "events", "news", "bureau", "trainings", "orders", "profile", "leaderboard"],
    accessToken: memberRealtimeToken,
    requiresAuth: false,
    onInvalidate: () => {
      void loadSession();
    }
  });

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      void loadSession();
    }, 300_000);

    return () => window.clearInterval(intervalId);
  }, [loadSession]);

  const loadPushConfiguration = useCallback(async () => {
    if (!user || !pushSupported) {
      setPushEnabled(false);
      setPushError(null);
      return;
    }

    try {
      const payload = await withStoredMemberSession((token) => fetchMemberPushConfiguration(token));

      if (!payload) {
        setPushEnabled(false);
        return;
      }

      setPushEnabled(typeof Notification !== 'undefined' && Notification.permission === 'granted' && payload.enabled);
      setPushError(null);
    } catch (caughtError) {
      setPushEnabled(false);
      setPushError(caughtError instanceof Error ? caughtError.message : "Impossible de charger l'état des notifications push.");
    }
  }, [pushSupported, user]);

  useEffect(() => {
    void loadPushConfiguration();
  }, [loadPushConfiguration]);

  const handleJoinChallenge = async (challengeId: string) => {
    setChallengeFeedback(null);
    setJoiningChallengeId(challengeId);

    try {
      const payload = await withStoredMemberSession((token) => joinMemberChallenge(token, challengeId));

      if (!payload) {
        navigate('/formulaires?tab=login&redirect=%2Fmembre%3Ftab%3Dchallenges');
        return;
      }

      setChallenges((current) => current.map((challenge) => (challenge.id === payload.id ? payload : challenge)));
      setChallengeFeedback({
        type: 'success',
        message: `Inscription confirmée pour "${payload.title}". Ton rang actuel est #${payload.myRank}.`,
      });
    } catch (caughtError) {
      setChallengeFeedback({
        type: 'error',
        message:
          caughtError instanceof Error
            ? caughtError.message
            : "Impossible de rejoindre ce défi pour le moment.",
      });
    } finally {
      setJoiningChallengeId(null);
    }
  };

  const handleEnablePushNotifications = async () => {
    if (!user) {
      navigate('/formulaires?tab=login&redirect=%2Fmembre%3Ftab%3Dsettings');
      return;
    }

    setPushRequesting(true);
    setPushError(null);

    try {
      const payload = await withStoredMemberSession(async (token) => {
        await enableMemberPushNotifications(token);
        return true;
      });

      if (!payload) {
        navigate('/formulaires?tab=login&redirect=%2Fmembre%3Ftab%3Dsettings');
        return;
      }

      await loadPushConfiguration();
    } catch (caughtError) {
      setPushError(caughtError instanceof Error ? caughtError.message : "Impossible d'activer les notifications push.");
    } finally {
      setPushRequesting(false);
    }
  };

  const handleDisablePushNotifications = async () => {
    if (!user) {
      return;
    }

    setPushRequesting(true);
    setPushError(null);

    try {
      const payload = await withStoredMemberSession(async (token) => {
        await disableMemberPushNotifications(token);
        return true;
      });

      if (!payload) {
        navigate('/formulaires?tab=login&redirect=%2Fmembre%3Ftab%3Dsettings');
        return;
      }

      setPushEnabled(false);
      setSettingsFeedback({
        type: 'success',
        message: 'Notifications push désactivées sur cet appareil.',
      });
      await loadPushConfiguration();
    } catch (caughtError) {
      setPushError(caughtError instanceof Error ? caughtError.message : "Impossible de désactiver les notifications push.");
    } finally {
      setPushRequesting(false);
    }
  };

  const handleAvatarChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    event.target.value = '';

    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      setSettingsFeedback({
        type: 'error',
        message: "Seules les images sont autorisées pour la photo de profil.",
      });
      return;
    }

    setSettingsFeedback(null);
    setUpdatingAvatar(true);

    try {
      const token = await withStoredMemberSession(async (accessToken) => accessToken);

      if (!token) {
        navigate('/formulaires?tab=login&redirect=%2Fmembre%3Ftab%3Dsettings');
        return;
      }

      const uploaded = await uploadPublicProfileImage(file);
      await updateMemberAvatar(token, uploaded.url);
      await loadSession();
      setSettingsFeedback({
        type: 'success',
        message: 'Photo de profil mise à jour avec succès.',
      });
    } catch (caughtError) {
      setSettingsFeedback({
        type: 'error',
        message:
          caughtError instanceof Error
            ? caughtError.message
            : "Impossible de mettre à jour votre photo de profil pour le moment.",
      });
    } finally {
      setUpdatingAvatar(false);
    }
  };

  const handleProfileUpdate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setProfileFeedback(null);

    if (profileForm.firstName.trim().length < 2 || profileForm.lastName.trim().length < 2) {
      setProfileFeedback({
        type: 'error',
        message: 'Le prénom et le nom doivent contenir au moins 2 caractères.',
      });
      return;
    }

    if (profileForm.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profileForm.email.trim())) {
      setProfileFeedback({
        type: 'error',
        message: 'Merci de saisir une adresse email valide.',
      });
      return;
    }

    setUpdatingProfile(true);

    try {
      const payload = await withStoredMemberSession(async (token) => ({
        token,
        user: await updateMemberProfile(token, {
          firstName: profileForm.firstName.trim(),
          lastName: profileForm.lastName.trim(),
          email: profileForm.email.trim() || null,
        }),
      }));

      if (!payload) {
        navigate('/formulaires?tab=login&redirect=%2Fmembre%3Ftab%3Dprofile');
        return;
      }

      const nextDashboard = dashboard?.profil
        ? {
            ...dashboard,
            profil: {
              ...dashboard.profil,
              prenom: payload.user.prenom,
              nom: payload.user.nom,
              email: payload.user.email,
            },
          }
        : dashboard;

      setUser(payload.user);
      persistMemberSession(payload.token, payload.user);
      setDashboard(nextDashboard ?? null);
      if (nextDashboard) {
        persistMemberDashboardSnapshot(nextDashboard);
      }
      setProfileFeedback({
        type: 'success',
        message: 'Profil mis à jour avec succès.',
      });
    } catch (caughtError) {
      setProfileFeedback({
        type: 'error',
        message: caughtError instanceof Error ? caughtError.message : 'Impossible de mettre à jour votre profil pour le moment.',
      });
    } finally {
      setUpdatingProfile(false);
    }
  };

  const refreshChallenges = async () => {
    setChallengeFeedback(null);
    setChallengesLoading(true);

    try {
      const payload = await withStoredMemberSession((token) => fetchMemberChallenges(token));

      if (!payload) {
        navigate('/formulaires?tab=login&redirect=%2Fmembre%3Ftab%3Dchallenges');
        return;
      }

      setChallenges(payload);
    } catch (caughtError) {
      setChallengeFeedback({
        type: 'error',
        message:
          caughtError instanceof Error
            ? caughtError.message
            : "Impossible d'actualiser les défis pour le moment.",
      });
    } finally {
      setChallengesLoading(false);
    }
  };

  const resetChallengeComposer = useCallback(() => {
    revokeChallengeImageObjectUrl();
    setEditingChallenge(null);
    setChallengeForm(createDefaultChallengeForm());
    setChallengeImageFile(null);
    setChallengeImagePreview(null);
    setChallengeImageRemoved(false);
  }, [revokeChallengeImageObjectUrl]);

  const handleStartEditChallenge = (challenge: PublicChallengeItem) => {
    revokeChallengeImageObjectUrl();
    setChallengeFeedback(null);
    setEditingChallenge(challenge);
    setChallengeForm(createChallengeFormFromItem(challenge));
    setChallengeImageFile(null);
    setChallengeImagePreview(challenge.image);
    setChallengeImageRemoved(false);
    challengeComposerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleCancelChallengeEdition = () => {
    resetChallengeComposer();
    setChallengeFeedback(null);
  };

  const handleDeleteChallenge = async (challengeId: string) => {
    setChallengeFeedback(null);
    setDeletingChallengeId(challengeId);

    try {
      const payload = await withStoredMemberSession(async (token) => {
        await deleteMemberChallenge(token, challengeId);
        const [memberChallenges, memberDashboard] = await Promise.all([
          fetchMemberChallenges(token),
          fetchMemberDashboard(token)
        ]);

        return {
          memberChallenges,
          memberDashboard
        };
      });

      if (!payload) {
        navigate('/formulaires?tab=login&redirect=%2Fmembre%3Ftab%3Dchallenges');
        return;
      }

      setChallenges(payload.memberChallenges);
      setDashboard(payload.memberDashboard);
      persistMemberDashboardSnapshot(payload.memberDashboard);
      if (editingChallenge?.id === challengeId) {
        resetChallengeComposer();
      }
      setChallengeFeedback({
        type: 'success',
        message: 'Votre défi a bien été supprimé.',
      });
    } catch (caughtError) {
      setChallengeFeedback({
        type: 'error',
        message:
          caughtError instanceof Error
            ? caughtError.message
            : "Impossible de supprimer ce défi pour le moment.",
      });
    } finally {
      setDeletingChallengeId(null);
    }
  };

  const handlePasswordChange = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSettingsFeedback(null);
    setPasswordErrors({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });

    const nextErrors = {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    };

    if (!shouldShowForcePasswordModal && !passwordForm.currentPassword.trim()) {
      nextErrors.currentPassword = 'Merci de renseigner votre mot de passe actuel.';
    }

    if (!passwordForm.newPassword.trim()) {
      nextErrors.newPassword = 'Merci de saisir un nouveau mot de passe.';
    } else {
      nextErrors.newPassword = getPasswordValidationError(passwordForm.newPassword) ?? '';
    }

    if (!passwordForm.confirmPassword.trim()) {
      nextErrors.confirmPassword = 'Merci de confirmer votre nouveau mot de passe.';
    } else if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      nextErrors.confirmPassword = 'La confirmation du nouveau mot de passe ne correspond pas.';
    }

    if (nextErrors.currentPassword || nextErrors.newPassword || nextErrors.confirmPassword) {
      setPasswordErrors(nextErrors);
      setSettingsFeedback({
        type: 'error',
        message: 'Corrige les champs du mot de passe en rouge.',
      });
      return;
    }

    setChangingPassword(true);

    try {
      const payload = await withStoredMemberSession(async (token) => {
        return changeMemberPassword(token, {
          ...(shouldShowForcePasswordModal ? {} : { currentPassword: passwordForm.currentPassword }),
          newPassword: passwordForm.newPassword,
        });
      });

      if (payload === null) {
        navigate('/formulaires?tab=login&redirect=%2Fmembre');
        return;
      }

      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setPasswordErrors({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });

      if (shouldShowForcePasswordModal) {
        persistMemberSession(payload.accessToken, payload.user);
        setUser(payload.user);

        navigate('/');
        return;
      }

      window.setTimeout(() => {
        void (async () => {
          try {
            await signOutMemberSession();
          } finally {
            navigate('/formulaires?tab=login&passwordUpdated=1');
          }
        })();
      }, 900);
    } catch (caughtError) {
      const message =
        caughtError instanceof Error
          ? caughtError.message
          : 'Impossible de modifier votre mot de passe pour le moment.';

      if (message.toLowerCase().includes('actuel') || message.toLowerCase().includes('incorrect')) {
        setPasswordErrors((current) => ({
          ...current,
          currentPassword: message,
        }));
      } else if (
        message.includes('8 caractères') ||
        message.includes('majuscule') ||
        message.includes('minuscule') ||
        message.includes('chiffre')
      ) {
        setPasswordErrors((current) => ({
          ...current,
          newPassword: message,
        }));
      } else if (message.toLowerCase().includes('confirmation') || message.toLowerCase().includes('correspond')) {
        setPasswordErrors((current) => ({
          ...current,
          confirmPassword: message,
        }));
      }

      if (shouldShowForcePasswordModal) {
        setSettingsFeedback({
          type: 'error',
          message,
        });
      } else {
        setSettingsFeedback({
          type: 'error',
          message,
        });
      }
    } finally {
      setChangingPassword(false);
    }
  };

  const updateChallengeForm = (field: keyof CreateMemberChallengeInput, value: string | number) => {
    setChallengeForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleChallengeImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    event.target.value = '';

    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      setChallengeFeedback({
        type: 'error',
        message: "Seules les images sont autorisées pour la photo du défi.",
      });
      return;
    }

    setChallengeFeedback(null);
    revokeChallengeImageObjectUrl();
    const previewUrl = URL.createObjectURL(file);
    challengeImageObjectUrlRef.current = previewUrl;
    setChallengeImageFile(file);
    setChallengeImagePreview(previewUrl);
    setChallengeImageRemoved(false);
  };

  const handleRemoveChallengeImage = () => {
    revokeChallengeImageObjectUrl();
    setChallengeImageFile(null);
    setChallengeImagePreview(null);
    setChallengeImageRemoved(true);
  };

  const handleSubmitChallenge = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setChallengeFeedback(null);
    setCreatingChallenge(true);

    try {
      const payload = await withStoredMemberSession(async (token) => {
        let imageValue: string | null | undefined;

        if (challengeImageFile) {
          const uploaded = await uploadCommunityImage(token, challengeImageFile);
          imageValue = uploaded.url;
        } else if (editingChallenge && challengeImageRemoved) {
          imageValue = null;
        }

        const challengePayload: CreateMemberChallengeInput = {
          ...challengeForm,
          ...(imageValue !== undefined ? { image: imageValue } : {}),
        };

        const created = editingChallenge
          ? await updateMemberChallenge(token, editingChallenge.id, challengePayload)
          : await createMemberChallenge(token, challengePayload);
        const [memberDashboard, memberChallenges] = await Promise.all([
          fetchMemberDashboard(token),
          fetchMemberChallenges(token),
        ]);

        return {
          created,
          memberDashboard,
          memberChallenges,
        };
      });

      if (!payload) {
        navigate('/formulaires?tab=login&redirect=%2Fmembre%3Ftab%3Dchallenges');
        return;
      }

      setDashboard(payload.memberDashboard);
      persistMemberDashboardSnapshot(payload.memberDashboard);
      setChallenges(payload.memberChallenges);
      const savedTitle = payload.created.title;
      resetChallengeComposer();
      setChallengeFeedback({
        type: 'success',
        message: editingChallenge
          ? `Le défi "${savedTitle}" a bien été mis à jour.`
          : `Le défi "${savedTitle}" a été créé et ajouté à ton espace membre.`,
      });
    } catch (caughtError) {
      setChallengeFeedback({
        type: 'error',
        message:
          caughtError instanceof Error
            ? caughtError.message
            : editingChallenge
              ? "Impossible de modifier ce défi pour le moment."
              : "Impossible de créer ce défi pour le moment.",
      });
    } finally {
      setCreatingChallenge(false);
    }
  };

  const memberStats = dashboard?.statistiques
    ? {
        availableEvents: dashboard.statistiques.availableEvents,
        eventsAttended: dashboard.statistiques.eventsAttended,
        challengesCompleted: dashboard.statistiques.challengesCompleted,
        squadsJoined: dashboard.statistiques.squadsJoined,
        teamsJoined: dashboard.statistiques.teamsJoined,
        points: dashboard.statistiques.points,
        rank: dashboard.statistiques.rank,
        nextRank: dashboard.statistiques.nextRank,
        pointsToNext: dashboard.statistiques.pointsToNext,
      }
    : fallbackStats;
  const collectiveMembershipsCount = memberStats.teamsJoined + memberStats.squadsJoined;
  const collectiveMembershipsLabel = `${memberStats.teamsJoined} équipe${memberStats.teamsJoined > 1 ? 's' : ''} • ${memberStats.squadsJoined} squad${memberStats.squadsJoined > 1 ? 's' : ''}`;
  const pointsTarget = memberStats.points + memberStats.pointsToNext;
  const rankProgress = pointsTarget > 0 ? Math.round((memberStats.points / pointsTarget) * 100) : 0;
  const canCreateChallenges = dashboard?.permissions.canCreateChallenges ?? true;
  const approvedSquadsCreated = dashboard?.permissions.approvedSquadsCreated ?? 0;
  const teamThreads = privateThreads.filter((thread) => thread.scope === 'TEAM');
  const squadThreads = privateThreads.filter((thread) => thread.scope === 'SQUAD');
  const createdChallenges = challenges.filter((challenge) => challenge.createdByMe);
  const memberProfile = dashboard?.profil
    ? dashboard.profil
    : user
      ? {
          id: user.id,
          nom: user.nom,
          prenom: user.prenom,
          email: user.email,
          matricule: user.matricule,
          telephone: user.telephone,
          departement: user.departement,
          sport: [],
          statut: user.statut,
          dateInscription: '',
          dateValidation: undefined,
          avatar: user.avatar,
          cotisation: user.profil?.cotisation ?? 'À jour'
        }
      : null;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      {/* Hero */}
      <section className="relative py-12 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=african%20professional%20office%20team%20senegal%20corporate%20modern%20workspace%20teal%20orange%20branding%20colleagues%20smiling&width=1600&height=300&seq=member-hero1&orientation=landscape"
            alt="Espace Membre"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-[#1A8C7A]/85"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="flex flex-col md:flex-row items-center gap-6">
          <button
            type="button"
            onClick={() => heroAvatarInputRef.current?.click()}
            className="group relative flex h-24 w-24 items-center justify-center overflow-hidden rounded-full border-2 border-white/40 transition-transform hover:scale-[1.02]"
            aria-label="Changer la photo de profil"
          >
            {user?.avatar ? (
              <img src={user.avatar} alt={user.prenom} className="h-full w-full object-cover object-[center_24%]" />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-white/15">
                <i className="ri-user-line text-5xl text-black"></i>
              </div>
            )}
            <div className="absolute inset-0 bg-[#0F172A]/0 transition-colors group-hover:bg-[#0F172A]/10"></div>
            <span className="absolute -bottom-1 -right-1 flex h-8 w-8 items-center justify-center rounded-full bg-[#E8521A] text-white shadow-sm">
              <i className="ri-camera-line text-sm"></i>
            </span>
            <input
              ref={heroAvatarInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(event) => void handleAvatarChange(event)}
              disabled={updatingAvatar}
            />
          </button>
          <div className="text-white text-center md:text-left">
            <h1 className="text-3xl font-bold mb-1">{user ? `${user.prenom} ${user.nom}` : 'Chargement...'}</h1>
            <p className="opacity-90 mb-2">Matricule: {user?.matricule ?? '—'}</p>
            <div className="flex items-center gap-2 justify-center md:justify-start">
              <span className="text-sm opacity-80">
                {memberStats.points} pts · {memberStats.rank}
              </span>
            </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Navigation */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <button
                onClick={() => changeTab('dashboard')}
                className={`w-full flex items-center gap-3 px-5 py-4 text-left transition-colors ${
                  activeTab === 'dashboard' ? 'bg-orange-50 text-orange-600' : 'hover:bg-gray-50'
                }`}
              >
                <i className="ri-dashboard-line text-xl"></i>
                Tableau de bord
              </button>
              <button
                onClick={() => changeTab('challenges')}
                className={`w-full flex items-center gap-3 px-5 py-4 text-left transition-colors ${
                  activeTab === 'challenges' ? 'bg-orange-50 text-orange-600' : 'hover:bg-gray-50'
                }`}
              >
                <i className="ri-trophy-line text-xl"></i>
                Mes défis
              </button>
              <button
                onClick={() => changeTab('profile')}
                className={`w-full flex items-center gap-3 px-5 py-4 text-left transition-colors ${
                  activeTab === 'profile' ? 'bg-orange-50 text-orange-600' : 'hover:bg-gray-50'
                }`}
              >
                <i className="ri-user-settings-line text-xl"></i>
                Mon profil
              </button>
              <button
                onClick={() => changeTab('orders')}
                className={`w-full flex items-center gap-3 px-5 py-4 text-left transition-colors ${
                  activeTab === 'orders' ? 'bg-orange-50 text-orange-600' : 'hover:bg-gray-50'
                }`}
              >
                <i className="ri-shopping-bag-line text-xl"></i>
                Mes commandes
              </button>
              <button
                onClick={() => changeTab('settings')}
                className={`w-full flex items-center gap-3 px-5 py-4 text-left transition-colors ${
                  activeTab === 'settings' ? 'bg-orange-50 text-orange-600' : 'hover:bg-gray-50'
                }`}
              >
                <i className="ri-settings-3-line text-xl"></i>
                Paramètres
              </button>
            </div>

            {/* Logout */}
            <button
              onClick={() => {
                void (async () => {
                  try {
                    await signOutMemberSession();
                  } finally {
                    navigate('/formulaires?tab=login');
                  }
                })();
              }}
              className="w-full flex items-center gap-3 px-5 py-4 text-red-600 hover:bg-red-50 rounded-xl transition-colors"
            >
              <i className="ri-logout-box-line text-xl"></i>
              Déconnexion
            </button>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3 space-y-8">
            {loading && (
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="h-6 w-40 rounded bg-gray-200 animate-pulse mb-4"></div>
                <div className="h-4 w-64 rounded bg-gray-100 animate-pulse"></div>
              </div>
            )}
            {sessionError && (
              <div className="bg-red-50 border border-red-100 text-red-700 rounded-xl p-4 text-sm">
                {sessionError}
              </div>
            )}
            {activeTab === 'dashboard' && (
              <>
                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-xl p-5 shadow-sm border-2 border-gray-200 h-full">
                    <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-3">
                      <i className="ri-calendar-event-line text-orange-600 text-xl"></i>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{memberStats.availableEvents}</p>
                    <p className="text-sm text-gray-600">Événements à venir</p>
                  </div>
                  <div className="bg-white rounded-xl p-5 shadow-sm border-2 border-gray-200 h-full">
                    <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center mb-3">
                      <i className="ri-trophy-line text-teal-600 text-xl"></i>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{memberStats.challengesCompleted}</p>
                    <p className="text-sm text-gray-600">Défis</p>
                  </div>
                  <div className="bg-white rounded-xl p-5 shadow-sm border-2 border-gray-200 h-full">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-3">
                      <i className="ri-team-line text-blue-600 text-xl"></i>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{collectiveMembershipsCount}</p>
                    <p className="text-sm text-gray-600">Équipes & squads</p>
                    <p className="mt-1 text-xs text-gray-500">{collectiveMembershipsLabel}</p>
                  </div>
                  <div className="bg-white rounded-xl p-5 shadow-sm border-2 border-gray-200 h-full">
                    <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-3">
                      <i className="ri-coin-line text-purple-600 text-xl"></i>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{memberStats.points}</p>
                    <p className="text-sm text-gray-600">Points</p>
                  </div>
                </div>

                {/* Progress */}
                <div className="bg-white rounded-xl p-6 shadow-sm border-2 border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-bold text-gray-900">Progression vers {memberStats.nextRank}</h3>
                      <p className="text-sm text-gray-600">Encore {memberStats.pointsToNext} points à gagner</p>
                    </div>
                    <span className="text-2xl font-bold text-orange-600">{rankProgress}%</span>
                  </div>
                  <div className="h-3 bg-gray-400 rounded-full overflow-hidden">
                    <div className="h-full bg-gray-800 rounded-full" style={{ width: `${rankProgress}%` }}></div>
                  </div>
                </div>

                {/* Shortcuts */}
                <div>
                  <h3 className="font-bold text-gray-900 mb-4">Raccourcis rapides</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {shortcuts.map(shortcut => (
                      <a
                        key={shortcut.id}
                        href={shortcut.link}
                        className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow text-center group"
                      >
                        <div className={`w-14 h-14 ${shortcut.color} rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform`}>
                          <i className={`${shortcut.icon} text-white text-2xl`}></i>
                        </div>
                        <p className="font-medium text-gray-900 text-sm">{shortcut.name}</p>
                      </a>
                    ))}
                  </div>
                </div>

                {/* Two Columns */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Upcoming Events */}
                  <div className="bg-white rounded-xl p-6 shadow-sm border-2 border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-bold text-gray-900">Prochains événements</h3>
                      <a href="/sport" className="text-sm text-orange-600 hover:text-orange-700">Voir tout</a>
                    </div>
                    <div className="space-y-4">
                      {upcomingEvents.length ? (
                        upcomingEvents.map(event => (
                          <div key={event.id} className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
                            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                              <i className={`${event.type === 'BUREAU_MEETING' ? 'ri-video-chat-line' : 'ri-calendar-line'} text-orange-600`}></i>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-medium text-gray-900 truncate">{event.titre}</p>
                                {event.type === 'BUREAU_MEETING' ? (
                                  <span className="rounded-full bg-sky-100 px-2 py-0.5 text-xs font-semibold text-sky-700">Réunion bureau</span>
                                ) : null}
                              </div>
                              <p className="text-sm text-gray-600">{formatEventDateTimeRange(event)} • {event.lieu}</p>
                              {event.type === 'BUREAU_MEETING' && event.meetingUrl ? (
                                <a
                                  href={event.meetingUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1A8C7A] hover:text-[#157a6a] hover:underline"
                                >
                                  <i className="ri-external-link-line"></i>
                                  Ouvrir le lien de réunion
                                </a>
                              ) : null}
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500">Aucun événement à venir.</p>
                      )}
                    </div>
                  </div>

                  {/* Recent Activity */}
                  <div className="bg-white rounded-xl p-6 shadow-sm border-2 border-gray-200">
                    <h3 className="font-bold text-gray-900 mb-4">Activité récente</h3>
                    <div className="space-y-4">
                      {recentActivities.length ? (
                        recentActivities.map(activity => (
                          <div key={activity.id} className="flex items-start gap-4">
                            <div
                              className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                                activity.type === 'event' ? 'bg-orange-500' : 'bg-blue-500'
                              }`}
                            ></div>
                            <div className="flex-1">
                              <p className="font-medium text-gray-900 text-sm">{activity.title}</p>
                              <p className="text-sm text-gray-500">{activity.date}</p>
                            </div>
                            <span className="text-sm px-2 py-1 rounded-full bg-gray-100 text-gray-700">
                              {activity.status}
                            </span>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500">Aucune activité récente.</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                  <div className="bg-white rounded-xl p-6 shadow-sm border-2 border-gray-200">
                    <div className="flex items-center justify-between gap-3 mb-4">
                      <div>
                        <h3 className="font-bold text-gray-900">Mon équipe</h3>
                        <p className="text-sm text-gray-600 mt-1">L’équipe dont tu fais partie</p>
                      </div>
                      <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
                        <i className="ri-shield-star-line text-teal-600 text-xl"></i>
                      </div>
                    </div>

                    {teamThreads.length ? (
                      <div className="space-y-3">
                        {teamThreads.map((thread) => (
                          <div key={thread.id} className="rounded-xl bg-gray-50 border border-gray-100 p-4">
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <p className="font-semibold text-gray-900 truncate">{thread.name}</p>
                                <p className="text-sm text-gray-500 mt-1">{thread.sport}</p>
                                <p className="text-xs text-gray-400 mt-2 line-clamp-2">
                                  {thread.lastMessage || 'Aucun message pour le moment'}
                                </p>
                              </div>
                              {thread.unread > 0 ? (
                                <span className="inline-flex min-w-6 items-center justify-center rounded-full bg-[#E8521A] px-2 py-1 text-xs font-bold text-white">
                                  {thread.unread}
                                </span>
                              ) : null}
                            </div>
                            <Link
                              to={`/social?tab=chat&chatId=${encodeURIComponent(thread.id)}`}
                              className="mt-4 inline-flex items-center gap-2 rounded-full bg-teal-50 px-4 py-2 text-sm font-medium text-teal-700 hover:bg-teal-100"
                            >
                              <i className="ri-chat-3-line"></i>
                              Entrer dans l&apos;équipe
                            </Link>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="rounded-xl border border-dashed border-gray-200 px-4 py-8 text-center text-sm text-gray-500">
                        Vous n&apos;êtes rattaché à aucune équipe pour le moment.
                      </div>
                    )}
                  </div>

                  <div className="bg-white rounded-xl p-6 shadow-sm border-2 border-gray-200">
                    <div className="flex items-center justify-between gap-3 mb-4">
                      <div>
                        <h3 className="font-bold text-gray-900">Mes squads</h3>
                        <p className="text-sm text-gray-600 mt-1">Les squads que tu as intégrés</p>
                      </div>
                      <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                        <i className="ri-team-line text-orange-600 text-xl"></i>
                      </div>
                    </div>

                    {squadThreads.length ? (
                      <div className="space-y-3">
                        {squadThreads.map((thread) => (
                          <div key={thread.id} className="rounded-xl bg-gray-50 border border-gray-100 p-4">
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <p className="font-semibold text-gray-900 truncate">{thread.name}</p>
                                <p className="text-sm text-gray-500 mt-1">{thread.sport}</p>
                                <p className="text-xs text-gray-400 mt-2 line-clamp-2">
                                  {thread.lastMessage || 'Aucun message pour le moment'}
                                </p>
                              </div>
                              {thread.unread > 0 ? (
                                <span className="inline-flex min-w-6 items-center justify-center rounded-full bg-[#E8521A] px-2 py-1 text-xs font-bold text-white">
                                  {thread.unread}
                                </span>
                              ) : null}
                            </div>
                            <Link
                              to={`/social?tab=chat&chatId=${encodeURIComponent(thread.id)}`}
                              className="mt-4 inline-flex items-center gap-2 rounded-full bg-orange-50 px-4 py-2 text-sm font-medium text-orange-700 hover:bg-orange-100"
                            >
                              <i className="ri-chat-3-line"></i>
                              Entrer dans le squad
                            </Link>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="rounded-xl border border-dashed border-gray-200 px-4 py-8 text-center text-sm text-gray-500">
                        Vous n&apos;avez rejoint aucun squad pour le moment.
                      </div>
                    )}
                  </div>

                  <div className="bg-white rounded-xl p-6 shadow-sm border-2 border-gray-200">
                    <div className="flex items-center justify-between gap-3 mb-4">
                      <div>
                        <h3 className="font-bold text-gray-900">Mes défis créés</h3>
                        <p className="text-sm text-gray-600 mt-1">Les défis que tu as lancés</p>
                      </div>
                      <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                        <i className="ri-trophy-line text-purple-600 text-xl"></i>
                      </div>
                    </div>

                    {createdChallenges.length ? (
                      <div className="space-y-3">
                        {createdChallenges.slice(0, 3).map((challenge) => (
                          <div key={challenge.id} className="rounded-xl bg-gray-50 border border-gray-100 p-4">
                            <div className="flex items-center justify-between gap-3">
                              <div className="min-w-0">
                                <p className="font-semibold text-gray-900 truncate">{challenge.title}</p>
                                <p className="text-sm text-gray-500 mt-1">
                                  {challenge.participants} participant{challenge.participants > 1 ? 's' : ''} • {challenge.sport}
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="inline-flex rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                                  Créé
                                </span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    changeTab('challenges');
                                    handleStartEditChallenge(challenge);
                                  }}
                                  className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-orange-100 bg-white text-orange-500 transition-colors hover:bg-orange-50"
                                  aria-label={`Modifier le défi ${challenge.title}`}
                                >
                                  <i className="ri-edit-line"></i>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => void handleDeleteChallenge(challenge.id)}
                                  disabled={deletingChallengeId === challenge.id}
                                  className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-red-100 bg-white text-red-500 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                                  aria-label={`Supprimer le défi ${challenge.title}`}
                                >
                                  <i className="ri-delete-bin-line"></i>
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => changeTab('challenges')}
                          className="inline-flex items-center gap-2 rounded-full bg-purple-50 px-4 py-2 text-sm font-medium text-purple-700 hover:bg-purple-100"
                        >
                          <i className="ri-external-link-line"></i>
                          Voir mes défis
                        </button>
                      </div>
                    ) : (
                      <div className="rounded-xl border border-dashed border-gray-200 px-4 py-8 text-center text-sm text-gray-500">
                        Aucun défi lancé pour le moment.
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-sm border-2 border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-gray-900">Dernières actualités</h3>
                    <a href="/actualites" className="text-sm text-orange-600 hover:text-orange-700">Voir tout</a>
                  </div>
                  <div className="space-y-4">
                    {dashboard?.latestNews?.length ? (
                      dashboard.latestNews.map((news) => (
                        <div key={news.id} className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
                          <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            <i className="ri-newspaper-line text-orange-600"></i>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 truncate">{news.titre}</p>
                            <p className="text-sm text-gray-600">{news.date} • {news.categorie}</p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-gray-500">Aucune actualité publiée pour le moment.</p>
                    )}
                  </div>
                </div>
              </>
            )}

            {activeTab === 'challenges' && (
              <div className="space-y-6">
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">Mes défis</h2>
                      <p className="text-sm text-gray-600 mt-1">
                        Les inscriptions, la création et le suivi de tes défis passent désormais par ton espace membre.
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      <button
                        type="button"
                        onClick={() => void refreshChallenges()}
                        disabled={challengesLoading}
                        className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        <i className="ri-refresh-line"></i>
                        {challengesLoading ? 'Actualisation...' : 'Actualiser'}
                      </button>
                      <Link
                        to="/social?tab=challenges"
                        className="inline-flex items-center gap-2 rounded-full bg-orange-50 px-4 py-2 text-sm font-medium text-orange-700 hover:bg-orange-100"
                      >
                        <i className="ri-external-link-line"></i>
                        Voir l&apos;interface dédiée
                      </Link>
                    </div>
                  </div>
                </div>

                <div ref={challengeComposerRef} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="max-w-2xl">
                      <div className="inline-flex items-center gap-2 rounded-full bg-orange-50 px-3 py-1 text-xs font-semibold text-orange-700">
                        <i className={isEditingChallenge ? 'ri-edit-line' : 'ri-trophy-line'}></i>
                        {isEditingChallenge ? 'Modification de défi' : 'Création de défi'}
                      </div>
                      <h3 className="mt-4 text-xl font-bold text-gray-900">
                        {isEditingChallenge ? 'Corriger ou compléter votre défi' : 'Défi et challenge: même objet métier'}
                      </h3>
                      {isEditingChallenge ? (
                        <p className="mt-2 text-sm text-gray-600">
                          Vous pouvez corriger le texte, changer l&apos;objectif, remplacer l&apos;image ou compléter les informations oubliées.
                        </p>
                      ) : null}
                    </div>
                    <div className="rounded-2xl bg-gray-50 px-4 py-3 text-sm text-gray-600">
                      {isEditingChallenge
                        ? "Les modifications seront visibles ensuite dans votre espace membre, dans le réseau social et dans les interfaces dédiées."
                        : approvedSquadsCreated > 0
                        ? `Tu peux créer des défis individuels et des défis portés par tes squads validés. Squads validés créés: ${approvedSquadsCreated}.`
                        : "Tu peux créer ici tes défis individuels. Si tu crées ensuite un squad validé, tes défis communautaires remonteront aussi dans les interfaces dédiées."}
                    </div>
                  </div>

                  {canCreateChallenges || isEditingChallenge ? (
                    <form className="mt-6 space-y-5" onSubmit={handleSubmitChallenge}>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="mb-2 block text-sm font-medium text-gray-700">Titre du défi</label>
                          <input
                            type="text"
                            value={challengeForm.title}
                            onChange={(event) => updateChallengeForm('title', event.target.value)}
                            placeholder="Ex: 5 km en 7 jours"
                            className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm text-gray-900 outline-none transition focus:border-orange-400"
                            required
                          />
                        </div>
                        <div>
                          <label className="mb-2 block text-sm font-medium text-gray-700">Sport</label>
                          <select
                            value={challengeForm.sport}
                            onChange={(event) => updateChallengeForm('sport', event.target.value)}
                            className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm text-gray-900 outline-none transition focus:border-orange-400"
                          >
                            {CHALLENGE_SPORT_OPTIONS.map((sport) => (
                              <option key={sport} value={sport}>
                                {sport}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="mb-2 block text-sm font-medium text-gray-700">Objectif chiffré</label>
                          <input
                            type="number"
                            min={1}
                            value={challengeForm.target}
                            onChange={(event) => updateChallengeForm('target', Number(event.target.value) || 0)}
                            className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm text-gray-900 outline-none transition focus:border-orange-400"
                            required
                          />
                        </div>
                        <div>
                          <label className="mb-2 block text-sm font-medium text-gray-700">Unité</label>
                          <input
                            type="text"
                            value={challengeForm.unit}
                            onChange={(event) => updateChallengeForm('unit', event.target.value)}
                            placeholder="Ex: pas/jour, km, pompes"
                            className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm text-gray-900 outline-none transition focus:border-orange-400"
                            required
                          />
                        </div>
                        <div>
                          <label className="mb-2 block text-sm font-medium text-gray-700">Récompense</label>
                          <input
                            type="text"
                            value={challengeForm.reward}
                            onChange={(event) => updateChallengeForm('reward', event.target.value)}
                            placeholder="Ex: Badge Or + 500 points"
                            className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm text-gray-900 outline-none transition focus:border-orange-400"
                            required
                          />
                        </div>
                        <div>
                          <label className="mb-2 block text-sm font-medium text-gray-700">Date limite</label>
                          <input
                            type="date"
                            value={challengeForm.deadline}
                            onChange={(event) => updateChallengeForm('deadline', event.target.value)}
                            className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm text-gray-900 outline-none transition focus:border-orange-400"
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <label className="mb-2 block text-sm font-medium text-gray-700">Description</label>
                        <textarea
                          value={challengeForm.description}
                          onChange={(event) => updateChallengeForm('description', event.target.value)}
                          rows={4}
                          placeholder="Décris le principe du défi, la durée et la manière de participer."
                          className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm text-gray-900 outline-none transition focus:border-orange-400"
                          required
                        />
                      </div>

                      <div>
                        <label className="mb-2 block text-sm font-medium text-gray-700">Photo du défi</label>
                        <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-4">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleChallengeImageChange}
                            className="block w-full text-sm text-gray-700 file:mr-4 file:rounded-full file:border-0 file:bg-orange-500 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-orange-600"
                          />
                          <p className="mt-2 text-xs text-gray-500">
                            Optionnel. Si vous ajoutez une image, elle sera affichée avec le défi dans les interfaces dédiées.
                          </p>

                          {challengeImagePreview ? (
                            <div className="mt-4 space-y-3">
                              <img
                                src={challengeImagePreview}
                                alt="Aperçu du défi"
                                className="h-44 w-full rounded-2xl object-cover"
                              />
                              <button
                                type="button"
                                onClick={handleRemoveChallengeImage}
                                className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
                              >
                                <i className="ri-delete-bin-line"></i>
                                Retirer la photo
                              </button>
                            </div>
                          ) : null}
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-3">
                        {isEditingChallenge ? (
                          <button
                            type="button"
                            onClick={handleCancelChallengeEdition}
                            className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-5 py-3 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
                          >
                            <i className="ri-close-line"></i>
                            Annuler la modification
                          </button>
                        ) : null}
                        <button
                          type="submit"
                          disabled={creatingChallenge}
                          className="inline-flex items-center gap-2 rounded-full bg-orange-500 px-5 py-3 text-sm font-semibold text-white transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          <i className={isEditingChallenge ? 'ri-save-line' : 'ri-add-line'}></i>
                          {creatingChallenge
                            ? isEditingChallenge
                              ? 'Mise à jour...'
                              : 'Publication...'
                            : isEditingChallenge
                              ? 'Mettre à jour ce défi'
                              : 'Publier ce défi'}
                        </button>
                        <p className="text-sm text-gray-500">
                          {isEditingChallenge
                            ? "Les changements seront appliqués sans perdre les participants déjà inscrits."
                            : "Le défi sera visible ensuite dans ton espace membre, dans le réseau social et dans les interfaces dédiées de l'application."}
                        </p>
                      </div>
                    </form>
                  ) : (
                    <div className="mt-6 rounded-2xl border border-dashed border-gray-200 bg-gray-50 px-5 py-4 text-sm text-gray-600">
                      La création de défi est limitée au membre qui a créé un squad validé. Cette règle garde un flux clair et une gouvernance propre en production.
                    </div>
                  )}
                </div>

                {challengeFeedback ? (
                  <div
                    className={`rounded-xl border px-4 py-3 text-sm ${
                      challengeFeedback.type === 'success'
                        ? 'border-emerald-100 bg-emerald-50 text-emerald-700'
                        : 'border-red-100 bg-red-50 text-red-600'
                    }`}
                  >
                    {challengeFeedback.message}
                  </div>
                ) : null}

                {challengesLoading ? (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="h-64 rounded-xl bg-gray-100 animate-pulse"></div>
                    <div className="h-64 rounded-xl bg-gray-100 animate-pulse"></div>
                  </div>
                ) : challenges.length ? (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {challenges.map((challenge) => {
                      const alreadyJoined = challenge.myRank > 0;

                      return (
                        <article
                          key={challenge.id}
                          className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 space-y-5"
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex items-start gap-4">
                              <div className={`w-14 h-14 rounded-2xl ${challenge.bgColor} flex items-center justify-center`}>
                                <i className={`${challenge.icon} ${challenge.color} text-2xl`}></i>
                              </div>
                              <div>
                                <div className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-600">
                                  {challenge.sport}
                                </div>
                                {challenge.createdByMe ? (
                                  <div className="mt-2 inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                                    <i className="ri-sparkling-line"></i>
                                    Créé par vous
                                  </div>
                                ) : null}
                                <h3 className="mt-3 text-xl font-bold text-gray-900">{challenge.title}</h3>
                                <p className="mt-2 text-sm text-gray-600">{challenge.description}</p>
                                {challenge.creatorName ? (
                                  <p className="mt-2 text-xs text-gray-500">Créateur: {challenge.creatorName}</p>
                                ) : null}
                              </div>
                            </div>
                            <span
                              className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold ${
                                alreadyJoined ? 'bg-emerald-100 text-emerald-700' : 'bg-orange-100 text-orange-700'
                              }`}
                            >
                              {alreadyJoined ? `Rang #${challenge.myRank}` : 'Inscription ouverte'}
                            </span>
                          </div>

                          {challenge.image ? (
                            <img
                              src={challenge.image}
                              alt={challenge.title}
                              className="h-48 w-full rounded-2xl object-cover"
                            />
                          ) : null}

                          <div className="grid grid-cols-2 gap-3">
                            <div className="rounded-xl bg-gray-50 px-4 py-3">
                              <p className="text-xs uppercase tracking-wide text-gray-500">Objectif</p>
                              <p className="mt-1 text-sm font-semibold text-gray-900">
                                {challenge.target.toLocaleString('fr-FR')} {challenge.unit}
                              </p>
                            </div>
                            <div className="rounded-xl bg-gray-50 px-4 py-3">
                              <p className="text-xs uppercase tracking-wide text-gray-500">Participants</p>
                              <p className="mt-1 text-sm font-semibold text-gray-900">{challenge.participants}</p>
                            </div>
                            <div className="rounded-xl bg-gray-50 px-4 py-3">
                              <p className="text-xs uppercase tracking-wide text-gray-500">Date limite</p>
                              <p className="mt-1 text-sm font-semibold text-gray-900">{formatChallengeDeadline(challenge.deadline)}</p>
                            </div>
                            <div className="rounded-xl bg-gray-50 px-4 py-3">
                              <p className="text-xs uppercase tracking-wide text-gray-500">Récompense</p>
                              <p className="mt-1 text-sm font-semibold text-gray-900">{challenge.reward}</p>
                            </div>
                          </div>

                          {alreadyJoined ? (
                            <div className="rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                              Tu participes déjà à ce défi. Score enregistré: {challenge.myScore} {challenge.unit}.
                            </div>
                          ) : (
                            <div className="rounded-xl border border-orange-100 bg-orange-50 px-4 py-3 text-sm text-orange-800">
                              Rejoins ce défi depuis ton espace membre pour que ton inscription et ton classement soient bien rattachés à ton compte.
                            </div>
                          )}

                          <div>
                            <h4 className="text-sm font-semibold text-gray-900 mb-3">Top classement</h4>
                            <div className="space-y-2">
                              {challenge.leaderboard.slice(0, 3).map((entry) => (
                                <div key={`${challenge.id}-${entry.rank}`} className="flex items-center gap-3 rounded-xl bg-gray-50 px-4 py-3">
                                  <span className="w-8 text-sm font-bold text-gray-400">
                                    {entry.badge || `#${entry.rank}`}
                                  </span>
                                  {entry.avatar ? (
                                    <img src={entry.avatar} alt={entry.name} className="w-9 h-9 rounded-full object-cover object-[center_24%]" />
                                  ) : (
                                    <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center text-xs font-semibold text-gray-500">
                                      {entry.name.charAt(0).toUpperCase()}
                                    </div>
                                  )}
                                  <span className="flex-1 text-sm font-medium text-gray-900">{entry.name}</span>
                                  <span className="text-sm font-semibold text-gray-700">{entry.score}</span>
                                </div>
                              ))}
                            </div>
                          </div>

                          {challenge.createdByMe ? (
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                              <button
                                type="button"
                                onClick={() => handleStartEditChallenge(challenge)}
                                className="w-full rounded-xl border border-orange-200 bg-orange-50 py-3 font-semibold text-orange-700 transition-colors hover:bg-orange-100"
                              >
                                {editingChallenge?.id === challenge.id ? 'Défi en cours de modification' : 'Modifier ce défi'}
                              </button>
                              <button
                                type="button"
                                onClick={() => void handleDeleteChallenge(challenge.id)}
                                disabled={deletingChallengeId === challenge.id}
                                className="w-full rounded-xl border border-red-200 bg-red-50 py-3 font-semibold text-red-600 transition-colors hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
                              >
                                {deletingChallengeId === challenge.id ? 'Suppression...' : 'Supprimer ce défi'}
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => void handleJoinChallenge(challenge.id)}
                              disabled={alreadyJoined || joiningChallengeId === challenge.id}
                              className="w-full rounded-xl bg-orange-500 text-white py-3 font-semibold hover:bg-orange-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                            >
                              {alreadyJoined
                                ? 'Déjà inscrit à ce défi'
                                : joiningChallengeId === challenge.id
                                  ? 'Inscription en cours...'
                                  : 'Rejoindre ce défi'}
                            </button>
                          )}
                        </article>
                      );
                    })}
                  </div>
                ) : (
                  <div className="bg-white rounded-xl p-8 shadow-sm border border-dashed border-gray-200 text-center text-gray-500">
                    Aucun défi n&apos;est disponible pour le moment.
                  </div>
                )}
              </div>
            )}

            {activeTab === 'profile' && (
              <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Mon profil</h2>
                <form className="space-y-6" onSubmit={handleProfileUpdate}>
                  {profileFeedback ? (
                    <div
                      className={`rounded-xl border px-4 py-3 text-sm ${
                        profileFeedback.type === 'success'
                          ? 'border-emerald-100 bg-emerald-50 text-emerald-700'
                          : 'border-red-100 bg-red-50 text-red-600'
                      }`}
                    >
                      {profileFeedback.message}
                    </div>
                  ) : null}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Prénom</label>
                      <input
                        type="text"
                        value={profileForm.firstName}
                        onChange={(event) => setProfileForm((current) => ({ ...current, firstName: event.target.value }))}
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-gray-700 focus:border-orange-400 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Nom</label>
                      <input
                        type="text"
                        value={profileForm.lastName}
                        onChange={(event) => setProfileForm((current) => ({ ...current, lastName: event.target.value }))}
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-gray-700 focus:border-orange-400 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                      <input
                        type="email"
                        value={profileForm.email}
                        onChange={(event) => setProfileForm((current) => ({ ...current, email: event.target.value }))}
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-gray-700 focus:border-orange-400 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone</label>
                      <input
                        type="tel"
                        readOnly
                        value={memberProfile?.telephone ?? ''}
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-gray-50 text-gray-700"
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={updatingProfile}
                    className="inline-flex items-center gap-2 rounded-xl bg-orange-500 px-8 py-3 font-semibold text-white transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <i className={updatingProfile ? 'ri-loader-4-line animate-spin' : 'ri-save-line'}></i>
                    {updatingProfile ? 'Mise à jour...' : 'Enregistrer les modifications'}
                  </button>
                </form>
              </div>
            )}

            {activeTab === 'orders' && (
              <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
                <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Mes commandes</h2>
                    <p className="mt-1 text-sm text-gray-500">Retrouvez vos achats boutique, leur paiement et l'avancement de livraison.</p>
                  </div>
                  <button
                    onClick={() => void loadSession()}
                    className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    <i className="ri-refresh-line"></i>
                    Actualiser
                  </button>
                </div>
                {ordersLoading ? (
                  <div className="space-y-4">
                    <div className="h-24 rounded-xl bg-gray-100 animate-pulse"></div>
                    <div className="h-24 rounded-xl bg-gray-100 animate-pulse"></div>
                  </div>
                ) : orders.length ? (
                  <div className="space-y-4">
                    {orders.map((order) => {
                      const status = formatStatus(order.status);
                      const payment = formatPaymentStatus(order.paymentStatus);
                      const currentStatusIndex = getOrderStatusIndex(order.status);
                      return (
                        <div key={order.id} className="border border-gray-100 rounded-xl p-5 bg-gray-50">
                          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                            <div>
                              <p className="font-semibold text-gray-900">Commande {order.code}</p>
                              <p className="text-sm text-gray-600">Date: {order.date}</p>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <span className={`text-sm px-3 py-1 rounded-full font-medium ${status.classes}`}>
                                {status.label}
                              </span>
                              <span className={`text-sm px-3 py-1 rounded-full font-medium ${payment.classes}`}>
                                {payment.label}
                              </span>
                            </div>
                          </div>
                          {order.paymentReference ? (
                            <div className="mt-3 rounded-xl border border-emerald-100 bg-white px-4 py-3 text-sm text-gray-700">
                              <span className="font-medium text-gray-900">Référence paiement :</span> {order.paymentReference}
                              {order.paymentProvider ? <span className="text-gray-500"> · {order.paymentProvider}</span> : null}
                            </div>
                          ) : null}
                          <div className="mt-4 space-y-2">
                            {order.items.map((item, index) => (
                              <div key={`${order.id}-${index}`} className="flex items-center justify-between text-sm text-gray-700">
                                <span>{item.name} x{item.quantity}</span>
                                <span>{formatMoney(item.unitPrice * item.quantity)}</span>
                              </div>
                            ))}
                          </div>
                          {order.status !== 'CANCELLED' ? (
                            <div className="mt-4 rounded-2xl border border-gray-100 bg-white px-4 py-4">
                              <div className="mb-3 text-xs font-medium uppercase tracking-wide text-gray-500">Suivi de livraison</div>
                              <div className="flex items-center gap-1">
                                {orderStatusFlow.map((step, index) => {
                                  const isDone = currentStatusIndex >= 0 && index <= currentStatusIndex;

                                  return (
                                    <div key={step.key} className="flex flex-1 items-center">
                                      <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs ${isDone ? 'bg-orange-500 text-white' : 'bg-gray-200 text-gray-500'}`}>
                                        <i className={step.icon}></i>
                                      </div>
                                      {index < orderStatusFlow.length - 1 ? (
                                        <div className={`h-1 flex-1 rounded-full ${index < currentStatusIndex ? 'bg-orange-500' : 'bg-gray-200'}`}></div>
                                      ) : null}
                                    </div>
                                  );
                                })}
                              </div>
                              <div className="mt-3 grid grid-cols-4 gap-2 text-[11px] text-gray-500">
                                {orderStatusFlow.map((step) => (
                                  <span key={step.key} className="text-center">{step.label}</span>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <div className="mt-4 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
                              Cette commande a été annulée.
                            </div>
                          )}
                          <div className="mt-4 flex items-center justify-between text-sm font-semibold text-gray-900">
                            <span>Total</span>
                            <span>{formatMoney(order.total)}</span>
                          </div>
                          <div className="mt-4 flex justify-end">
                            <button
                              type="button"
                              onClick={() => setSelectedOrder(order)}
                              className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-white px-4 py-2 text-sm font-medium text-orange-600 hover:bg-orange-50"
                            >
                              <i className="ri-eye-line"></i>
                              Détails
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-shopping-bag-line text-3xl text-gray-400"></i>
                    </div>
                    <p className="text-gray-500">Vous n'avez pas encore de commandes</p>
                    <a href="/boutique" className="inline-block mt-4 text-orange-600 font-medium hover:text-orange-700">
                      Découvrir la boutique
                    </a>
                  </div>
                )}
              </div>
            )}

            {selectedOrder ? (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setSelectedOrder(null)}>
                <div
                  className="w-full max-w-3xl max-h-[85vh] overflow-hidden rounded-[28px] border border-gray-100 bg-white shadow-[0_30px_100px_rgba(0,0,0,0.16)]"
                  onClick={(event) => event.stopPropagation()}
                >
                  <div className="flex items-center justify-between border-b border-gray-100 px-6 py-5">
                    <div>
                      <h3 className="text-lg font-bold text-[#1A1A1A]">Commande {selectedOrder.code}</h3>
                      <p className="mt-1 text-sm text-gray-500">Détail des produits achetés et de leur paiement.</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSelectedOrder(null)}
                      className="flex h-9 w-9 items-center justify-center rounded-xl text-gray-500 transition-colors hover:bg-gray-100"
                    >
                      <i className="ri-close-line text-lg"></i>
                    </button>
                  </div>

                  <div className="max-h-[70vh] space-y-5 overflow-auto p-6">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                      <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
                        <div className="text-xs text-gray-500">Statut commande</div>
                        <div className={`mt-2 inline-flex rounded-full px-3 py-1 text-sm font-medium ${formatStatus(selectedOrder.status).classes}`}>
                          {formatStatus(selectedOrder.status).label}
                        </div>
                      </div>
                      <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
                        <div className="text-xs text-gray-500">Statut paiement</div>
                        <div className={`mt-2 inline-flex rounded-full px-3 py-1 text-sm font-medium ${formatPaymentStatus(selectedOrder.paymentStatus).classes}`}>
                          {formatPaymentStatus(selectedOrder.paymentStatus).label}
                        </div>
                      </div>
                      <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
                        <div className="text-xs text-gray-500">Référence</div>
                        <div className="mt-2 text-sm font-semibold text-gray-900">{selectedOrder.paymentReference ?? 'Non disponible'}</div>
                      </div>
                    </div>

                    {selectedOrder.status !== 'CANCELLED' ? (
                      <div className="rounded-2xl border border-gray-100 bg-white p-4">
                        <div className="mb-3 text-sm font-semibold text-[#1A1A1A]">Avancement de la commande</div>
                        <div className="flex items-center gap-1">
                          {orderStatusFlow.map((step, index) => {
                            const currentStatusIndex = getOrderStatusIndex(selectedOrder.status);
                            const isDone = currentStatusIndex >= 0 && index <= currentStatusIndex;

                            return (
                              <div key={step.key} className="flex flex-1 items-center">
                                <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm ${isDone ? 'bg-orange-500 text-white' : 'bg-gray-200 text-gray-500'}`}>
                                  <i className={step.icon}></i>
                                </div>
                                {index < orderStatusFlow.length - 1 ? (
                                  <div className={`h-1.5 flex-1 rounded-full ${index < currentStatusIndex ? 'bg-orange-500' : 'bg-gray-200'}`}></div>
                                ) : null}
                              </div>
                            );
                          })}
                        </div>
                        <div className="mt-3 grid grid-cols-4 gap-2 text-xs text-gray-500">
                          {orderStatusFlow.map((step) => (
                            <span key={step.key} className="text-center">{step.label}</span>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
                        Cette commande a été annulée et ne sera pas expédiée.
                      </div>
                    )}

                    <div className="space-y-4">
                      {selectedOrder.items.map((item, index) => {
                        const fallback = item.productId ? orderProductFallbacks[item.productId] : null;
                        const resolvedImage = item.imageUrl || fallback?.image || "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 640 400'><rect width='640' height='400' fill='%23F7F7F5'/><rect x='32' y='32' width='576' height='336' rx='32' fill='%23FFF3ED'/><circle cx='190' cy='144' r='44' fill='%23E8521A' fill-opacity='0.25'/><path d='M96 312l118-108 94 82 86-68 150 94' fill='none' stroke='%23E8521A' stroke-width='26' stroke-linecap='round' stroke-linejoin='round'/></svg>";
                        const resolvedDescription = item.description || fallback?.description || "Aucune description enregistrée pour ce produit au moment de la commande.";
                        const resolvedCategory = item.category || fallback?.category || null;
                        const resolvedBadge = item.badge || fallback?.badge || null;

                        return (
                          <div key={`${selectedOrder.id}-${index}`} className="rounded-2xl border border-gray-100 bg-[#FAFAF8] p-4">
                            <div className="flex flex-col gap-4 md:flex-row">
                              <img
                                src={resolvedImage}
                                alt={item.name}
                                className="h-28 w-full rounded-2xl object-cover md:w-28"
                              />
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-start justify-between gap-3">
                                <div>
                                  <h4 className="text-base font-bold text-[#1A1A1A]">{item.name}</h4>
                                  <div className="mt-2 flex flex-wrap gap-2">
                                    {resolvedCategory ? (
                                      <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs text-gray-600">{resolvedCategory}</span>
                                    ) : null}
                                    {resolvedBadge ? (
                                      <span className="rounded-full bg-orange-100 px-2.5 py-1 text-xs font-medium text-[#E8521A]">{resolvedBadge}</span>
                                    ) : null}
                                  </div>
                                </div>
                                <div className="text-right">
                                  <div className="text-sm text-gray-500">Quantité</div>
                                  <div className="text-lg font-bold text-[#E8521A]">x{item.quantity}</div>
                                </div>
                              </div>
                              <p className="mt-3 text-sm leading-6 text-gray-600">{resolvedDescription}</p>
                              <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm">
                                <span className="text-gray-500">Prix unitaire : <span className="font-semibold text-gray-900">{formatMoney(item.unitPrice)}</span></span>
                                <span className="text-gray-500">Sous-total : <span className="font-bold text-[#1A1A1A]">{formatMoney(item.unitPrice * item.quantity)}</span></span>
                              </div>
                            </div>
                          </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="flex items-center justify-between rounded-2xl bg-orange-50 px-5 py-4">
                      <span className="font-semibold text-gray-900">Total commande</span>
                      <span className="text-lg font-bold text-[#E8521A]">{formatMoney(selectedOrder.total)}</span>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            {activeTab === 'settings' && (
              <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Paramètres</h2>
                <div className="space-y-6">
                  <div className="rounded-2xl border border-gray-100 bg-gray-50/70 p-5">
                    <div className="flex items-start justify-between gap-4 mb-5">
                      <div>
                        <p className="font-semibold text-gray-900">Photo de profil</p>
                        <p className="text-sm text-gray-600 mt-1">
                          Change ta photo à tout moment. Elle sera visible dans ton espace membre et dans les discussions.
                        </p>
                      </div>
                      <div className="w-11 h-11 rounded-xl bg-teal-100 text-teal-700 flex items-center justify-center flex-shrink-0">
                        <i className="ri-image-edit-line text-xl"></i>
                      </div>
                    </div>

                    {settingsFeedback ? (
                      <div
                        className={`mb-4 rounded-xl border px-4 py-3 text-sm ${
                          settingsFeedback.type === 'success'
                            ? 'border-emerald-100 bg-emerald-50 text-emerald-700'
                            : 'border-red-100 bg-red-50 text-red-600'
                        }`}
                      >
                        {settingsFeedback.message}
                      </div>
                    ) : null}

                    <div className="flex flex-col gap-5 md:flex-row md:items-center">
                      <div className="flex h-24 w-24 items-center justify-center overflow-hidden rounded-full border-4 border-white bg-orange-100 shadow-sm">
                        {user?.avatar ? (
                          <img src={user.avatar} alt={user.prenom ?? 'Membre'} className="h-full w-full object-cover object-[center_24%]" />
                        ) : (
                          <span className="text-2xl font-bold text-orange-600">
                            {`${user?.prenom?.[0] ?? ''}${user?.nom?.[0] ?? ''}`.trim() || 'M'}
                          </span>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">{user ? `${user.prenom} ${user.nom}` : 'Membre ASC'}</div>
                        <div className="mt-1 text-sm text-gray-500">Formats image acceptés. Idéalement carré pour un meilleur rendu.</div>
                        <label className="mt-4 inline-flex cursor-pointer items-center gap-2 rounded-full bg-orange-500 px-5 py-3 text-sm font-semibold text-white transition hover:bg-orange-600">
                          <i className={`${updatingAvatar ? 'ri-loader-4-line animate-spin' : 'ri-upload-2-line'}`}></i>
                          {updatingAvatar ? 'Mise à jour...' : 'Changer ma photo'}
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(event) => void handleAvatarChange(event)}
                            disabled={updatingAvatar}
                          />
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-gray-100 bg-gray-50/70 p-5">
                    <div className="flex items-start justify-between gap-4 mb-5">
                      <div>
                        <p className="font-semibold text-gray-900">Notifications push</p>
                        <p className="text-sm text-gray-600 mt-1">
                          Reçois les publications de l&apos;administration et les messages de tes squads et équipes, même hors de l&apos;application.
                        </p>
                      </div>
                      <div className="w-11 h-11 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center flex-shrink-0">
                        <i className="ri-notification-3-line text-xl"></i>
                      </div>
                    </div>

                    <div className="rounded-2xl border border-orange-100 bg-white px-4 py-4">
                      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900">
                            {pushEnabled ? 'Notifications push activées' : 'Activer les notifications push'}
                          </p>
                          <p className="mt-1 text-sm text-gray-500">
                            {pushSupported
                              ? pushEnabled
                                ? 'Les alertes importantes et les nouveaux messages privés seront envoyés sur cet appareil.'
                                : 'Autorise ce navigateur pour recevoir les alertes importantes et les nouveaux messages privés.'
                              : "Ce navigateur ne prend pas en charge les notifications push."}
                          </p>
                        </div>
                        {pushSupported ? (
                          pushEnabled ? (
                            <button
                              type="button"
                              onClick={() => void handleDisablePushNotifications()}
                              disabled={pushRequesting}
                              className="inline-flex items-center justify-center gap-2 rounded-full border border-red-100 bg-red-50 px-5 py-3 text-sm font-semibold text-red-600 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              <i className={pushRequesting ? 'ri-loader-4-line animate-spin' : 'ri-notification-off-line'}></i>
                              {pushRequesting ? 'Désactivation...' : 'Désactiver'}
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => void handleEnablePushNotifications()}
                              disabled={pushRequesting}
                              className="inline-flex items-center justify-center gap-2 rounded-full bg-orange-500 px-5 py-3 text-sm font-semibold text-white transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              <i className={pushRequesting ? 'ri-loader-4-line animate-spin' : 'ri-notification-3-line'}></i>
                              {pushRequesting ? 'Activation...' : 'Activer'}
                            </button>
                          )
                        ) : null}
                      </div>

                      {pushError ? (
                        <p className="mt-3 text-sm font-medium text-red-600">{pushError}</p>
                      ) : null}
                    </div>
                  </div>

                  {!forcePasswordChange ? (
                  <div className="rounded-2xl border border-gray-100 bg-gray-50/70 p-5">
                    <div className="flex items-start justify-between gap-4 mb-5">
                      <div>
                        <p className="font-semibold text-gray-900">Changer mon mot de passe</p>
                        <p className="text-sm text-gray-600 mt-1">
                          Sécurise ton espace membre en définissant un nouveau mot de passe.
                        </p>
                      </div>
                      <div className="w-11 h-11 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center flex-shrink-0">
                        <i className="ri-lock-password-line text-xl"></i>
                      </div>
                    </div>

                    <form className="grid grid-cols-1 md:grid-cols-2 gap-4" onSubmit={handlePasswordChange}>
                      <div className="md:col-span-2">
                        <PasswordInputField
                          label="Mot de passe actuel"
                          value={passwordForm.currentPassword}
                          onChange={(event) => {
                            setPasswordForm((current) => ({ ...current, currentPassword: event.target.value }));
                            setPasswordErrors((current) => ({ ...current, currentPassword: '' }));
                          }}
                          error={passwordErrors.currentPassword}
                          className={getPasswordInputClassName(Boolean(passwordErrors.currentPassword))}
                          autoComplete="current-password"
                        />
                      </div>
                      <PasswordInputField
                        label="Nouveau mot de passe"
                        value={passwordForm.newPassword}
                        onChange={(event) => {
                          setPasswordForm((current) => ({ ...current, newPassword: event.target.value }));
                          setPasswordErrors((current) => ({ ...current, newPassword: '' }));
                        }}
                        error={passwordErrors.newPassword}
                        className={getPasswordInputClassName(Boolean(passwordErrors.newPassword))}
                        autoComplete="new-password"
                      />
                      <PasswordInputField
                        label="Confirmer le nouveau mot de passe"
                        value={passwordForm.confirmPassword}
                        onChange={(event) => {
                          setPasswordForm((current) => ({ ...current, confirmPassword: event.target.value }));
                          setPasswordErrors((current) => ({ ...current, confirmPassword: '' }));
                        }}
                        error={passwordErrors.confirmPassword}
                        className={getPasswordInputClassName(Boolean(passwordErrors.confirmPassword))}
                        autoComplete="new-password"
                      />
                      <div className="md:col-span-2 flex flex-wrap items-center gap-3">
                        <button
                          type="submit"
                          disabled={changingPassword}
                          className="inline-flex items-center gap-2 rounded-full bg-orange-500 px-5 py-3 text-sm font-semibold text-white transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          <i className={`${changingPassword ? 'ri-loader-4-line animate-spin' : 'ri-shield-keyhole-line'}`}></i>
                          {changingPassword ? 'Mise à jour...' : 'Mettre à jour le mot de passe'}
                        </button>
                      </div>
                    </form>
                  </div>
                  ) : null}

                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {shouldShowForcePasswordModal ? (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-[#103A37]/65 px-4 py-6 backdrop-blur-[2px]">
          <div className="w-full max-w-xl overflow-hidden rounded-[28px] border border-[#1A8C7A]/15 bg-white shadow-[0_30px_100px_rgba(9,63,58,0.28)]">
            <div className="bg-[#117A6D] px-6 py-6 text-white">
              <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-white/16">
                <i className="ri-lock-password-line text-2xl"></i>
              </div>
              <h2 className="text-2xl font-bold">Choisissez votre nouveau mot de passe</h2>
              <p className="mt-2 text-sm text-white/90">
                Pour sécuriser votre première connexion, vous devez définir un nouveau mot de passe avant de continuer.
              </p>
            </div>

            <div className="px-6 py-6">
              {settingsFeedback ? (
                <div
                  className={`mb-5 rounded-2xl border px-4 py-3 text-sm ${
                    settingsFeedback.type === 'success'
                      ? 'border-emerald-100 bg-emerald-50 text-emerald-700'
                      : 'border-red-100 bg-red-50 text-red-600'
                  }`}
                >
                  {settingsFeedback.message}
                </div>
              ) : null}

              <form className="space-y-4" onSubmit={handlePasswordChange}>
                <PasswordInputField
                  label="Nouveau mot de passe"
                  value={passwordForm.newPassword}
                  onChange={(event) => {
                    setPasswordForm((current) => ({ ...current, newPassword: event.target.value }));
                    setPasswordErrors((current) => ({ ...current, newPassword: '' }));
                  }}
                  error={passwordErrors.newPassword}
                  className={getPasswordInputClassName(Boolean(passwordErrors.newPassword))}
                  autoComplete="new-password"
                  placeholder="Au moins 8 caractères, une majuscule et un chiffre"
                />
                <PasswordInputField
                  label="Confirmer le nouveau mot de passe"
                  value={passwordForm.confirmPassword}
                  onChange={(event) => {
                    setPasswordForm((current) => ({ ...current, confirmPassword: event.target.value }));
                    setPasswordErrors((current) => ({ ...current, confirmPassword: '' }));
                  }}
                  error={passwordErrors.confirmPassword}
                  className={getPasswordInputClassName(Boolean(passwordErrors.confirmPassword))}
                  autoComplete="new-password"
                  placeholder="Retapez le nouveau mot de passe"
                />

                <button
                  type="submit"
                  disabled={changingPassword}
                  className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[#E8521A] px-5 py-3.5 text-sm font-semibold text-white transition hover:bg-[#d44a16] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <i className={`${changingPassword ? 'ri-loader-4-line animate-spin' : 'ri-shield-keyhole-line'}`}></i>
                  {changingPassword ? 'Enregistrement...' : 'Enregistrer mon nouveau mot de passe'}
                </button>
              </form>
            </div>
          </div>
        </div>
      ) : null}
      <Footer />
    </div>
  );
}
